import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { useClipboard } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconCheck,
  IconExternalLink,
  IconFileText,
  IconLink,
  IconQuote,
} from "@tabler/icons-react";
import type { TFunction } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { logClick, useDocumentHits, useNames } from "../../api/hooks";
import type { DocumentSummary, Fragment, SearchGroup, SearchMode } from "../../api/types";
import { formatDate } from "../../lib/format";
import { snippetToHtml } from "../../lib/snippet";

interface Props {
  group: SearchGroup;
  rank: number;
  searchId: number;
  q: string;
  mode: SearchMode;
}

/**
 * Link into the viewer: query and mode drive its hit list (the same hits as the result card),
 * and `from` — the results page's own query string — brings the user back to the same list,
 * filters included.
 */
export function documentLink(
  doc: DocumentSummary,
  q: string,
  page?: number,
  mode: SearchMode = "hybrid",
): string {
  const p = new URLSearchParams();
  if (q) p.set("q", q);
  if (page) p.set("page", String(page));
  if (mode !== "hybrid") p.set("mode", mode);
  const from = window.location.pathname === "/" ? window.location.search.replace(/^\?/, "") : "";
  if (from) p.set("from", from);
  const s = p.toString();
  return `/documents/${doc.id}${s ? `?${s}` : ""}`;
}

export function DocumentHeader({ doc }: { doc: DocumentSummary }) {
  const { t } = useTranslation();
  const { company, docType } = useNames();
  const lowQuality = doc.textQuality !== null && doc.textQuality < 0.6;
  return (
    <Group gap="xs" wrap="wrap">
      {doc.docTypeId && <Badge variant="light">{docType(doc.docTypeId)}</Badge>}
      {doc.regNumber && (
        <Text size="sm" fw={500}>
          № {doc.regNumber}
        </Text>
      )}
      {doc.docDate && <Text size="sm">{formatDate(doc.docDate)}</Text>}
      <Text size="sm" c="dimmed">
        {company(doc.companyId)}
      </Text>
      <Text size="sm" c="dimmed">
        {t("search.pages", { count: doc.pageCount })}
      </Text>
      {doc.ocrPages > 0 && (
        <Tooltip label={t("search.ocrHint", { count: doc.ocrPages })}>
          <Badge color="orange" variant="light">
            OCR
          </Badge>
        </Tooltip>
      )}
      {lowQuality && (
        <Tooltip label={t("search.lowQualityHint")}>
          <Badge color="red" variant="light">
            {t("search.lowQuality")}
          </Badge>
        </Tooltip>
      )}
      {doc.status === "superseded" && (
        <Badge color="gray" variant="light">
          {t("search.superseded")}
        </Badge>
      )}
    </Group>
  );
}

/** Plain-text citation for the clipboard: document, number, date, page, clause and the quote. */
export function citationText(
  doc: DocumentSummary,
  fragment: Fragment,
  docTypeName: string | null,
  t: TFunction,
): string {
  const head = [
    docTypeName,
    doc.regNumber ? `№ ${doc.regNumber}` : null,
    doc.docDate ? formatDate(doc.docDate) : null,
  ]
    .filter(Boolean)
    .join(", ");
  const where = [
    t("search.page", { n: fragment.pageNo }),
    fragment.clause ? t("search.clause", { n: fragment.clause }) : null,
  ]
    .filter(Boolean)
    .join(", ");
  const quote = fragment.snippetHtml
    .replace(/<\/?mark>/g, "")
    .replace(/\s+/g, " ")
    .trim();
  const link = `${window.location.origin}${documentLink(doc, "", fragment.pageNo)}`;
  return `«${doc.title}»${head ? ` (${head})` : ""}, ${where}: «${quote}»\n${link}`;
}

function FragmentRow({
  fragment,
  doc,
  q,
  mode,
  onOpen,
}: {
  fragment: Fragment;
  doc: DocumentSummary;
  q: string;
  mode: SearchMode;
  onOpen(chunkId: number): void;
}) {
  const { t } = useTranslation();
  const { docType } = useNames();
  const clipboard = useClipboard({ timeout: 1500 });
  return (
    <Group align="flex-start" gap="sm" wrap="nowrap">
      <Stack gap={0} style={{ minWidth: 64, flexShrink: 0 }}>
        <Anchor
          component={Link}
          to={documentLink(doc, q, fragment.pageNo, mode)}
          onClick={() => onOpen(fragment.chunkId)}
          size="sm"
          fw={500}
          style={{ whiteSpace: "nowrap" }}
        >
          {t("search.page", { n: fragment.pageNo })}
        </Anchor>
        {fragment.clause && (
          <Text size="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
            {t("search.clause", { n: fragment.clause })}
          </Text>
        )}
      </Stack>
      <Text
        size="sm"
        style={{ flex: 1, lineHeight: 1.5 }}
        // snippetToHtml escapes everything except <mark>
        dangerouslySetInnerHTML={{ __html: snippetToHtml(fragment.snippetHtml) }}
      />
      {fragment.semantic && !fragment.lexical && (
        <Tooltip label={t("search.semanticHint")}>
          <Badge size="xs" variant="outline" color="grape" style={{ flexShrink: 0 }}>
            {t("search.semantic")}
          </Badge>
        </Tooltip>
      )}
      <Tooltip label={t("search.copyCitation")}>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          style={{ flexShrink: 0 }}
          aria-label={t("search.copyCitation")}
          onClick={() => {
            clipboard.copy(
              citationText(doc, fragment, doc.docTypeId ? docType(doc.docTypeId) : null, t),
            );
            notifications.show({ message: t("search.citationCopied"), color: "green" });
          }}
        >
          {clipboard.copied ? <IconCheck size={14} /> : <IconQuote size={14} />}
        </ActionIcon>
      </Tooltip>
    </Group>
  );
}

export function ResultGroup({ group, rank, searchId, q, mode }: Props) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const hits = useDocumentHits(expanded ? group.document.id : null, q, mode);
  const doc = group.document;
  const shown = expanded && hits.data ? hits.data.fragments : group.fragments;
  const more = group.hitCount - group.fragments.length;
  const onOpen = (chunkId?: number) => logClick(searchId, doc.id, chunkId, rank);
  const clipboard = useClipboard({ timeout: 1500 });
  const copyLink = () => {
    const href = `${window.location.origin}${documentLink(doc, q, group.fragments[0]?.pageNo, mode)}`;
    clipboard.copy(href);
    notifications.show({ message: t("document.linkCopied"), color: "green" });
  };

  return (
    <Card withBorder padding="md" className="result-card">
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Anchor
            component={Link}
            to={documentLink(doc, q, group.fragments[0]?.pageNo, mode)}
            onClick={() => onOpen(group.fragments[0]?.chunkId)}
            fw={600}
            size="md"
            style={{ lineHeight: 1.3 }}
          >
            <Group gap={6} wrap="nowrap" align="center">
              <IconFileText size={18} style={{ flexShrink: 0 }} />
              <span>{doc.title}</span>
            </Group>
          </Anchor>
          <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
            <Badge variant="light" color="gray">
              {t("search.hits", { count: group.hitCount })}
            </Badge>
            <Tooltip label={t("document.copyLinkHint")}>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label={t("document.copyLink")}
                onClick={copyLink}
              >
                {clipboard.copied ? <IconCheck size={14} /> : <IconLink size={14} />}
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
        <DocumentHeader doc={doc} />
        <Stack gap={6}>
          {shown.map((f) => (
            <FragmentRow key={f.chunkId} fragment={f} doc={doc} q={q} mode={mode} onOpen={onOpen} />
          ))}
        </Stack>
        <Group gap="xs">
          {more > 0 && !expanded && (
            <Button variant="subtle" size="compact-xs" onClick={() => setExpanded(true)}>
              {t("search.moreFragments", { count: more })}
            </Button>
          )}
          {expanded && hits.isLoading && (
            <Text size="xs" c="dimmed">
              {t("common.loading")}
            </Text>
          )}
          {expanded && hits.data && (
            <Button variant="subtle" size="compact-xs" onClick={() => setExpanded(false)}>
              {t("search.lessFragments")}
            </Button>
          )}
          <Button
            component="a"
            href={`/api/documents/${doc.id}/original`}
            variant="subtle"
            size="compact-xs"
            leftSection={<IconExternalLink size={14} />}
            onClick={() => onOpen()}
          >
            {t("search.download")}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
